'use strict'

import Utils from './Utils.js'
import _escape from 'lodash/escape'
import { sha256 } from 'js-sha256'
import XMLParser from './XMLParser.js'
import { DOM } from './ui/DOM.js'
import { Selector } from './ui/Selectors.js'
import Color from './models/Color.js'

class Export {
    /**
     * Constructor
     *
     * @param {*} applicationName   ex: com.colorix.colorix
     * @param {*} applicationUUID   ex: lkdze82z3lsuw9
     * @param {*} applicationExt    ex: cxcox
     */
    constructor(applicationName, applicationUUID, applicationExt) {
        console.warn('New Export')

        this._BASE64_HEADER = 'data:image/jpg;base64,'

        this._domDocument = ''
        this._XMLFile = {}
        this.XMLFile.project = {}

        this._extensionFile = applicationExt

        // TODO Créer méthode
        this._appIdArray = [
            {
                'com.colorix.colorix': 'lkdze82z3lsuw9',
            },
            {
                [applicationName]: applicationUUID,
            },
        ]

        this.simulator = {}
        this.simulator.colorixExchangeDocument = {}
        this.simulator.colorixExchangeDocument.signature = applicationUUID
        this.simulator.colorixExchangeDocument.appID = applicationName
        this.simulator.colorixExchangeDocument.version = '1.0'
    }

    get extensionFile() {
        return this._extensionFile
    }

    set XMLFile(v) {
        this._XMLFile = v
    }

    get XMLFile() {
        return this._XMLFile
    }

    /**
     * From history / array of points, create an white image and draw for each visible color its points
     * @returns {string} base64 image data in grayscale
     */
    getBase64Canvas(canvasState) {
        console.log('getBase64Canvas')

        // Create empty image
        let canvas = document.createElement('canvas')
        let ctx = canvas.getContext('2d')

        canvas.id = 'CursorLayer'
        canvas.width = canvasState.viewimage_original.width
        canvas.height = canvasState.viewimage_original.height

        let imgData = ctx.getImageData(0, 0, canvasState.viewimage_original.width, canvasState.viewimage_original.height)

        // decalage des couleurs du mask
        for (let i = 0; i < canvasState._maskImage.length; i++) {
            const pos = i * 4
            imgData.data[pos] = canvasState._maskImage.data[i]
            imgData.data[pos + 1] = canvasState._maskImage.data[i]
            imgData.data[pos + 2] = canvasState._maskImage.data[i]
            imgData.data[pos + 3] = 255
        }

        // put the modified pixels back on the canvas
        ctx.putImageData(imgData, 0, 0)

        // TODO jpeg?
        // Image format and quality
        const jpegUrl = canvas.toDataURL('image/png', 1.0)

        if (jpegUrl !== 'undefined') {
            return jpegUrl.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
        } else {
            return null
        }
    }

    /**
     * Import project's elements to Project Class and return masks
     * @param {XML} document
     * @param {*} self
     */
    loadProjectFromFile(allText) {
        console.log('loadProjectFromFile')

        // USe the DOMParser browser API to convert text to a Document
        this._domDocument = new DOMParser().parseFromString(allText, 'text/xml')

        // Store XML
        this.XMLFile = new XMLParser().parse(this._domDocument)

        // ----------------------------------------------------------------------------------
        // Get colorixExchangeDocument
        // ----------------------------------------------------------------------------------

        this.XMLFile.project.name = this.XMLFile.project.attributes.name
        this.XMLFile.project.firstname = this.XMLFile.project.attributes.firstname
        this.XMLFile.project.lastname = this.XMLFile.project.attributes.lastname
        this.XMLFile.project.locationStreet1 = this.XMLFile.project.attributes.locationStreet1
        this.XMLFile.project.locationCity = this.XMLFile.project.attributes.locationCity
        this.XMLFile.project.locationState = this.XMLFile.project.attributes.locationState
        this.XMLFile.project.locationCP = this.XMLFile.project.attributes.locationCP
        this.XMLFile.project.locationCountry = this.XMLFile.project.attributes.locationCountry
        this.XMLFile.project.notes = this.XMLFile.project.attributes.notes
    }

    /**
     * Build a canvas-ready payload (DTO) from the already-parsed XML.
     *
     * DOES NOT change parsing logic; only normalizes what CanvasState needs:
     *  - Ensures `masks` is always an array (possibly empty).
     *  - Applies your base64 header to the main image.
     *  - Keeps mask base64/format as-is, present only if provided.
     *
     * @throws {Error} If the embedded photo image is missing.
     * @returns {{ imageB64: string, maskB64: string|null, maskFormat: string, masks: Array<any> }}
     */
    buildCanvasPhotoDTO() {
        if (!this.XMLFile || !this.XMLFile.project) {
            throw new Error('XML not loaded: call loadProjectFromFile() first.')
        }

        const proj = this.XMLFile.project
        const photo = proj.photo || {}

        // Main image (required)
        const imageText = photo.photoImageData?.text || null
        if (!imageText) throw new Error('Malformed XML: missing embedded image data.')

        // Optional mask sprite
        const hasPhotoMask = typeof photo.photoMaskData !== 'undefined' && photo.photoMaskData !== ''
        const maskB64 = hasPhotoMask ? photo.photoMaskData?.text || null : null
        const maskFormat = hasPhotoMask ? photo.photoMaskData?.attributes?.format || 'png' : 'png'

        // Normalize masks to array
        let masks = []
        if (typeof photo.masks !== 'undefined' && photo.masks !== '') {
            masks = Array.isArray(photo.masks) ? photo.masks : [photo.masks]
        }

        return {
            imageB64: this._BASE64_HEADER + imageText,
            maskB64,
            maskFormat,
            masks,
        }
    }

    getOriginalPicture() {
        // Create empty image
        let canvas = DOM.get(Selector.canvasOriginal)
        let img = canvas.toDataURL('image/jpeg', 1.0)
        return img.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
    }

    getSignatureFromDocument() {
        let colorSign = ''
        let node

        // For each Photos in a project
        for (let j = 0; j < this._domDocument.getElementsByTagName('project')[0].getElementsByTagName('photo').length; j++) {
            let color_sample = this._domDocument
                .getElementsByTagName('project')[0]
                .getElementsByTagName('photo')
                [j].getElementsByTagName('colorSample')
            // TODO: remove this in next xml exportation version
            let mask = this._domDocument.getElementsByTagName('project')[0].getElementsByTagName('photo')[j].getElementsByTagName('masks')

            // TODO: remove this in next xml exportation version
            // Get masks data
            if (mask.length > 0) {
                for (let i = 0; i < mask[j].getElementsByTagName('mask').length; i++) {
                    node = mask[j].getElementsByTagName('mask')[i]
                    colorSign += node.getAttributeNode('swatchColorCode').nodeValue.replace(/[\n\r]/g, '')
                    colorSign += node.getAttributeNode('swatchName').nodeValue
                }
            }

            // And colorsample data
            if (color_sample.length > 0) {
                for (let i = 0; i < color_sample[j].getElementsByTagName('color').length; i++) {
                    node = color_sample[j].getElementsByTagName('color')[i]
                    colorSign += node.getAttributeNode('swatchColorCode').nodeValue.replace(/[\n\r]/g, '')
                    colorSign += node.getAttributeNode('swatchName').nodeValue
                }
            }
        }

        this.XMLFile.colorixExchangeDocument = {}
        this.XMLFile.colorixExchangeDocument.appID = this._domDocument
            .getElementsByTagName('colorixExchangeDocument')[0]
            .getAttributeNode('appID').nodeValue
        this.XMLFile.colorixExchangeDocument.version = this._domDocument
            .getElementsByTagName('colorixExchangeDocument')[0]
            .getAttributeNode('version').nodeValue
        this.XMLFile.colorixExchangeDocument.signature = this._domDocument
            .getElementsByTagName('colorixExchangeDocument')[0]
            .getAttributeNode('signature').nodeValue

        this.XMLFile.project.photo.photoSignature = this._domDocument
            .getElementsByTagName('project')[0]
            .getElementsByTagName('photo')[0]
            .getAttributeNode('photoSignature').nodeValue
        this.XMLFile.project.projectUUID = this._domDocument.getElementsByTagName('project')[0].getAttributeNode('projectUUID').nodeValue
        this.XMLFile.project.name = this._domDocument.getElementsByTagName('project')[0].getAttributeNode('name').nodeValue

        const key = this._appIdArray.filter(Utils.filterAppID, this.XMLFile.colorixExchangeDocument.appID)[0][
            this.XMLFile.colorixExchangeDocument.appID
        ]
        const signature =
            key + this.XMLFile.project.projectUUID + this.XMLFile.project.name + this.XMLFile.project.photo.photoSignature + colorSign

        // Encrypt
        const ciphertext = sha256(signature)

        /* console.log(key);
        console.log(signature);
        console.log(ciphertext); */
        return ciphertext
    }

    getSignature(colorSamplesd, colorSampleUUID_P) {
        let colorSign = ''

        // TODO: remove this in next xml exportation version
        // MASK FIRST
        colorSamplesd.forEach(function (color) {
            console.log(color)
            if (typeof color !== 'undefined') {
                const { swatchColorCode } = color.ensureExportSwatchData()
                colorSign += swatchColorCode.replace(/[\n\r]/g, '')
                colorSign += color.swatch.name
            }
        })

        // THEN COLORSAMPLE
        colorSamplesd.forEach(function (color) {
            console.log(color)

            if (typeof color !== 'undefined') {
                const { swatchColorCode } = color.ensureExportSwatchData()
                colorSign += swatchColorCode.replace(/[\n\r]/g, '')
                colorSign += color.swatch.name
            }
        })

        const key = this._appIdArray.filter(Utils.filterAppID, this.simulator.colorixExchangeDocument.appID)[0][
            this.simulator.colorixExchangeDocument.appID
        ]
        const signature = key + this.XMLFile.project.projectUUID + this.XMLFile.project.name + colorSampleUUID_P + colorSign

        // Encrypt
        const ciphertext = sha256(signature)

        /* console.log(key);
        console.log(colorSampleUUID_P);
        console.log(colorSign);
        console.log(signature);
        console.log(ciphertext); */

        return ciphertext
    }

    /**
     * Get image dimensions
     * @param {*} file
     */
    getImageDimensions(file) {
        console.log('getImageDimensions')
        return new Promise(function (resolved) {
            let i = new Image()
            i.onload = function () {
                resolved({
                    w: i.width,
                    h: i.height,
                })
            }
            i.src = file
        })
    }

    /**
     * Check the XML document signature only.
     * Returns true if valid, false otherwise. Does NOT render.
     *
     * @returns {boolean}
     */
    checkDocumentSignature() {
        console.log('checkDocumentSignature')

        const signatureFromDocument = this.getSignatureFromDocument()
        const expected = this.XMLFile?.colorixExchangeDocument?.signature

        if (typeof signatureFromDocument !== 'string' || typeof expected !== 'string') {
            console.log('Cannot import project (missing signature)')
            return false
        }

        const ok = signatureFromDocument.localeCompare(expected) === 0
        console.log(ok ? 'Project signature OK' : 'Project signature mismatch')
        return ok
    }

    async initExportFromDatabase(data) {
        console.log('initExportFromDatabase')

        this.XMLFile.project = {
            name: data.name,
            firstname: data.firstname,
            lastname: data.lastname,
            notes: data.notes,
            projectUUID: Utils.export_generate_uuid('web', this.simulator.colorixExchangeDocument.appID, 'project'),
            creationDate: data.created_at,
            modificationDate: data.updated_at,
        }

        if (data.address) {
            this.XMLFile.project.locationStreet1 = data.address.street1
            this.XMLFile.project.locationStreet2 = data.address.street2
            this.XMLFile.project.locationCity = data.address.city
            this.XMLFile.project.locationState = data.address.state
            this.XMLFile.project.locationCP = data.address.npa
            if (data.address.country) {
                this.XMLFile.project.locationCountry = data.address.country.code
            }
            if (data.address.location_point) {
                this.XMLFile.project.locationLongitude = data.address.location_point.longitude
                this.XMLFile.project.locationLatitude = data.address.location_point.latitude
            }
        }
    }

    export_template(mode, canvasState) {
        console.log('export_template mode:' + mode)

        // sorted colors
        const colorSamplesd = new Map([...canvasState.colorSample.colors].sort((a, b) => a[0] - b[0]))
        const colorSampleUUID_P = Utils.export_generate_uuid('web', this.simulator.colorixExchangeDocument.appID, 'photo')

        let text = '<?xml version="1.0" encoding="UTF-8"?>\n'

        // ------------------------------------------------------------------
        // User information
        // ------------------------------------------------------------------

        const project = this.XMLFile.project || {}
        const readInputValue = (selector) => {
            const el = DOM.get(selector)
            const raw = el && 'value' in el ? el.value : null
            if (raw == null) return null
            const trimmed = String(raw).trim()
            return trimmed === '' ? null : trimmed
        }
        const encodeValue = (val) => Utils.encodeHTML(String(val ?? ''))
        const setProjectField = (key, selector) => {
            const inputVal = readInputValue(selector)
            if (inputVal != null) {
                project[key] = encodeValue(inputVal)
            } else if (project._encoded) {
                project[key] = project[key] ?? ''
            } else {
                project[key] = encodeValue(project[key] ?? '')
            }
        }

        const countrySelect = DOM.get(Selector.countryIDInput)
        let countryVal = ''
        if (countrySelect && countrySelect.options.length > 0) {
            const selectedIndex = countrySelect.selectedIndex
            if (selectedIndex >= 0 && countrySelect.options[selectedIndex]) {
                countryVal = countrySelect.options[selectedIndex].dataset.iso || ''
            }
        }

        setProjectField('name', Selector.nameInput)
        setProjectField('firstname', Selector.firstnameInput)
        setProjectField('lastname', Selector.lastnameInput)
        setProjectField('locationStreet1', Selector.addressInput)
        setProjectField('locationCity', Selector.cityInput)
        setProjectField('locationState', Selector.stateInput)
        setProjectField('locationCP', Selector.npaInput)
        project.locationCountry = countryVal ? encodeValue(countryVal) : project._encoded ? project.locationCountry ?? '' : encodeValue(project.locationCountry ?? '')
        setProjectField('notes', Selector.notesInput)
        project.locationStreet2 = project.locationStreet2 ?? ''
        project.locationLongitude = project.locationLongitude ?? ''
        project.locationLatitude = project.locationLatitude ?? ''
        project._encoded = true
        this.XMLFile.project = project

        this.XMLFile.project.projectUUID = Utils.export_generate_uuid('web', this.simulator.colorixExchangeDocument.appID, 'project')
        this.XMLFile.project.modificationDate = Utils.getCurrentTime()
        this.simulator.colorixExchangeDocument.signature = this.getSignature(colorSamplesd, colorSampleUUID_P)

        // ------------------------------------------------------------------
        // Build xml file
        // ------------------------------------------------------------------

        text +=
            '<colorixExchangeDocument signature="' +
            this.simulator.colorixExchangeDocument.signature +
            '" appID="' +
            this.simulator.colorixExchangeDocument.appID +
            '" version="' +
            this.simulator.colorixExchangeDocument.version +
            '">\n'
        text +=
            '<project name="' +
            this.XMLFile.project.name +
            '" projectUUID="' +
            this.XMLFile.project.projectUUID +
            '" creationDate="' +
            Utils.getCurrentTime() +
            '" modificationDate="' +
            this.XMLFile.project.modificationDate +
            '" '
        text +=
            'locationStreet1="' +
            this.XMLFile.project.locationStreet1 +
            '" locationStreet2="' +
            this.XMLFile.project.locationStreet2 +
            '" locationCity="' +
            this.XMLFile.project.locationCity +
            '" locationState="' +
            this.XMLFile.project.locationState +
            '" locationCP="' +
            this.XMLFile.project.locationCP +
            '" locationCountry="' +
            this.XMLFile.project.locationCountry +
            '" locationLongitude="' +
            this.XMLFile.project.locationLongitude +
            '" locationLatitude="' +
            this.XMLFile.project.locationLatitude +
            '" notes="' +
            this.XMLFile.project.notes +
            '">\n'

        // ------------------------------------------------------------------
        // Photo
        // ------------------------------------------------------------------

        text +=
            '<photo name="photo" creationDate="' +
            Utils.getCurrentTime() +
            '" modificationDate="' +
            Utils.getCurrentTime() +
            '" photoSignature="' +
            colorSampleUUID_P +
            '" >\n'

        if (mode === 1) {
            text += '<photoImageData format="jpg">' + this.getOriginalPicture() + '</photoImageData>\n'

            // Check if masks exist in canvasState
            const getBase64Canvas = this.getBase64Canvas(canvasState)

            if (getBase64Canvas) {
                text += '<photoMaskData format="png">' + getBase64Canvas + '</photoMaskData>\n'
            }
        }

        // TODO: remove this in next xml exportation version
        // ---------------------------------------------
        // Masks
        // ---------------------------------------------

        if (colorSamplesd.size > 0) {
            text += '<masks>\n'
            colorSamplesd.forEach((color, key) => {
                // TODO implement textureswatch in exportation file
                // example
                //  <color swatchName="RAL Classic" swatchServerID="78" swatchColorCode="RAL 2000" swatchColorInfos="color infos in base64"  swatchColorName="optionalName">
                //     <colorImage format="png/jpg">image png data in base64</colorImage>
                //     <texture textureName="" textureID="" textureGroupName="" textureGroupID=""  textureType="AlphaBlending/GrayBlending" format="png/jpg">image png data in base64</texture>
                // </color>

                // Empty color
                if (typeof color === 'undefined') {
                    text +=
                        '<mask luminosity=""' +
                        ' name=""' +
                        ' note=""' +
                        ' swatchName=""' +
                        ' swatchServerID=""' +
                        ' swatchColorCode=""' +
                        ' swatchColorName=""' +
                        ' swatchColorInfos=""/>\n'
                } else {
                    const { swatchColorCode, swatchColorName, swatchColorInfos } = color.ensureExportSwatchData()
                    text += '<mask luminosity="' + color.bleach_value + '"'

                    // swatch_color with texture
                    if (color.type !== Color.TYPE.COLOR) {
                        text +=
                            ' textureScale="' +
                            color.texture_scale +
                            '"' +
                            ' textureRotation="' +
                            color.texture_rotation +
                            '"' +
                            ' texturePixelResolution="' +
                            color.texturePixelResolution +
                            '"'
                    }

                    text +=
                        ' name="' +
                        color.name +
                        '"' +
                        ' note="' +
                        color.note +
                        '"' +
                        ' swatchName="' +
                        color.swatch.name +
                        '"' +
                        ' swatchServerID="' +
                        color.swatch.id +
                        '"' +
                        ' swatchColorCode="' +
                        swatchColorCode +
                        '"' +
                        ' swatchColorName="' +
                        _escape(swatchColorName) +
                        '"' +
                        ' swatchColorInfos="' +
                        swatchColorInfos +
                        '">\n'

                    // swatch_color with image
                    if (color.type === Color.TYPE.TEXTURE && mode === 1) {
                        text += '<colorImage format="' + 'png' + '">'
                        text += color.texture_data_base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
                        text += '</colorImage>\n'

                        // Texture
                    } else if (color.type === Color.TYPE.TEXTURE_SWATCH) {
                        text +=
                            '<texture textureName="' +
                            color.texture.name +
                            '"' + // texture name
                            ' textureSwatchID="' +
                            color.texture_swatch.id +
                            '"' + // TODO atm only used by webapp
                            ' textureID="' +
                            color.texture.id +
                            '"' + // TODO atm position of texture in texturegroup. After update -> textureSwatchID
                            ' textureGroupName="' +
                            color.texture_page.name +
                            '"' + // texturegroup name
                            ' textureGroupID="' +
                            color.texture_page.id +
                            '"' +
                            ' textureType="' +
                            'AlphaBlending' +
                            '"' +
                            ' format="' +
                            'png' +
                            '">'
                        if (mode === 1) {
                            text += color.texture_data_base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
                        }
                        text += '</texture>\n'
                    }
                    text += '</mask>\n'
                }
            })
            text += '</masks>\n'
        }

        // -------------------------------------------------------
        // ColorSamples
        // Contrairement au masks, les colorsamples ne peuvent
        // contenir des elements vides (swatchcolors vide)
        // et ne contiennt pas de propriétés variables (scale, etc)
        // -------------------------------------------------------

        if (colorSamplesd.size > 0) {
            text += '<colorSample creationDate="' + Utils.getCurrentTime() + '">\n'
            colorSamplesd.forEach((color, key) => {
                if (typeof color !== 'undefined') {
                    const { swatchColorCode, swatchColorName, swatchColorInfos } = color.ensureExportSwatchData()
                    text +=
                        '<color swatchName="' +
                        color.swatch.name +
                        '"' +
                        ' swatchServerID="' +
                        color.swatch.id +
                        '"' +
                        ' swatchColorCode="' +
                        swatchColorCode +
                        '"' +
                        ' swatchColorName="' +
                        _escape(swatchColorName) +
                        '"' +
                        ' swatchColorInfos="' +
                        swatchColorInfos +
                        '">'

                    // swatch_color with image
                    if (color.type === Color.TYPE.TEXTURE && mode === 1) {
                        text += '<colorImage format="' + 'png' + '">'
                        text += color.texture_data_base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
                        text += '</colorImage>\n'

                        // Texture
                    } else if (color.type === Color.TYPE.TEXTURE_SWATCH) {
                        text +=
                            '<texture textureName="' +
                            color.texture.name +
                            '"' + // texture name
                            ' textureSwatchID="' +
                            color.texture_swatch.id +
                            '"' + // TODO atm only used by webapp
                            ' textureID="' +
                            color.texture.id +
                            '"' + // TODO atm position of texture in texturegroup. After update -> textureSwatchID
                            ' textureGroupName="' +
                            color.texture_page.name +
                            '"' + // texturegroup name
                            ' textureGroupID="' +
                            color.texture_page.id +
                            '"' +
                            ' textureType="' +
                            'AlphaBlending' +
                            '"' +
                            ' format="' +
                            'png' +
                            '">'
                        if (mode === 1) {
                            text += color.texture_data_base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '')
                        }
                        text += '</texture>\n'
                    }
                    text += '</color>\n'
                }
            })
            text += '</colorSample>\n'
        }

        text += '</photo>\n'
        text += '</project>\n'
        text += '</colorixExchangeDocument>'
        text = text.replace(/undefined/g, '')
        return text
    }
}
export default Export
